import { useCallback } from "react";
import { getPlayerGames, PlayerGameOutHttp } from "@riot-private/riotpay-sdk-pmc";
import siteConfig from "@/variables";
import { useSessionStorage } from "react-use";
import { ThemeName } from "@/style/themes";

export type GameInfo = {
  name: string;
  shard?: string;
};

export function useGamesPlayed() {
  const environment = siteConfig.BUILD_ENV;
  const [gamesPlayed, setGamesPlayed] = useSessionStorage<PlayerGameOutHttp[]>(siteConfig.KEY_REDEEM_GAMES_PLAYED, []);

  const fetchPlayerGames = useCallback(
    async (rsoToken: string) => {
      if (!rsoToken) {
        throw new Error("RSO access token is required");
      }

      const games = await getPlayerGames(environment, "rgi", rsoToken);
      setGamesPlayed(games);
      return games;
    },
    [environment, setGamesPlayed],
  );

  const getGameInfo = useCallback(
    (theme: ThemeName): GameInfo => {
      // RiotPay SDK uses "2xko" instead of "lion"
      const serverName = theme === "lion" ? "2xko" : theme;
      const game = gamesPlayed.find((g) => g.game.toLowerCase() === serverName.toLowerCase());

      return {
        name: serverName,
        shard: game?.affinities?.[0],
      };
    },
    [gamesPlayed],
  );

  const isGamePlayable = useCallback(
    (theme: ThemeName): boolean => {
      if (!gamesPlayed || gamesPlayed.length === 0) {
        return false;
      }

      const { name } = getGameInfo(theme);
      return gamesPlayed.some((g) => g.game.toLowerCase() === name.toLowerCase());
    },
    [gamesPlayed],
  );

  return {
    getGameInfo,
    isGamePlayable,
    fetchPlayerGames,
    setGamesPlayed,
  };
}
